import React, { useState } from 'react'
import {
  Manager,
  Popper,
  PopperProps,
  Reference,
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  RefHandler,
} from 'react-popper'
import { Overlay } from './Overlay'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const css = require('./Popover.scss')

interface PopoverProps {
  /**
   * The target element that triggers the opening of the popover (button, input, etc.)
   */
  children: React.ReactNode | TargetRenderProps
  /**
   * Function that returns the content rendered into the popover.
   * TODO: Content may not care to get access to close fn, so should allow for
   * a regular ReactNode to be passed here
   */
  content: (props: {
    /**
     * Function that closes the popover
     */
    close: () => void
  }) => React.ReactNode
  /**
   * Position of popover in relation to the target element
   * @default bottom
   */
  placement?: PopperProps['placement']
}

type TargetRenderProps = (props: {
  /** Ref to attach to the target element (pass to reactstrap as `innerRef` prop) */
  ref: RefHandler
  /**
   * TODO: update so we can handle elements besides buttons (i.e., inputs)
   */
  onClick: (e: React.SyntheticEvent) => void
}) => React.ReactNode

/**
 * Component to display content next to a target element. Build on top of Popper.js and popper-react.
 */
export function Popover({
  content,
  children,
  placement: propPlacement = 'bottom',
}: PopoverProps) {
  const [isOpen, setOpen] = useState(false)
  const toggle = () => setOpen((prevOpen) => !prevOpen)

  /** Ideally, this would not have to handle function props, but certain components
   * (i.e., Reactstrap buttons) don't pass the ref through and use props like `innerRef`.
   * In order to support that type of component, we allow for function props. See datepicker.stories
   * for an example.
   */
  const getTarget = (ref: RefHandler) => {
    if (typeof children === 'function') {
      return children({ ref, onClick: toggle })
    } else {
      const target = React.Children.only(children)
      return React.cloneElement(target as React.ReactElement, {
        ref,
        onClick: toggle,
      })
    }
  }

  return (
    <Manager>
      <Reference>{({ ref }) => getTarget(ref)}</Reference>
      <Overlay isOpen={isOpen} onClose={() => setOpen(false)}>
        <Popper placement={propPlacement}>
          {({ ref, style, placement }) => (
            <div
              ref={ref}
              style={style}
              className={`bootstrap4 ${css.popover}`}
              data-placement={placement}
            >
              {content({ close: () => setOpen(false) })}
            </div>
          )}
        </Popper>
      </Overlay>
    </Manager>
  )
}
